/**
 * useRCAPolling Hook
 *
 * Polls the database for RCA and SMART goals generation status.
 * Automatically refreshes when background processing completes.
 *
 * Usage:
 * ```tsx
 * const rcaStatus = useRCAPolling(sessionId, true);
 *
 * if (rcaStatus.isGenerating) {
 *   // Show loading indicator
 * }
 *
 * if (rcaStatus.isComplete) {
 *   // Refresh assessment data
 * }
 * ```
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface RCAStatus {
  rcaStatus: string;
  rcaCompletedAt: string | null;
  smartStatus: string;
  smartCompletedAt: string | null;
  isGenerating: boolean;
  isComplete: boolean;
  lastChecked: Date;
}

const POLL_INTERVAL_MS = 3000; // Poll every 3 seconds

/**
 * Hook to poll for RCA and SMART goals generation status
 *
 * @param sessionId - The session ID to poll for
 * @param enabled - Whether polling is enabled (default: true)
 * @returns RCAStatus object with current generation status
 */
export function useRCAPolling(sessionId: string | undefined, enabled = true): RCAStatus {
  const [status, setStatus] = useState<RCAStatus>({
    rcaStatus: 'pending',
    rcaCompletedAt: null,
    smartStatus: 'pending',
    smartCompletedAt: null,
    isGenerating: false,
    isComplete: false,
    lastChecked: new Date(),
  });

  const checkStatus = useCallback(async () => {
    if (!sessionId || !enabled) return;

    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(
          'rca_generation_status, rca_generation_completed_at, smart_goals_generation_status, smart_goals_generation_completed_at'
        )
        .eq('id', sessionId)
        .single();

      if (error || !data) {
        console.warn('[useRCAPolling] Failed to fetch session status:', error);
        return;
      }

      const rcaStatus = data.rca_generation_status || 'pending';
      const smartStatus = data.smart_goals_generation_status || 'pending';

      // Determine if generation is still in progress
      const isGenerating =
        ['pending', 'in_progress'].includes(rcaStatus) ||
        ['pending', 'in_progress'].includes(smartStatus);

      // Determine if both are complete (success or failure)
      const isComplete =
        ['completed', 'skipped', 'failed'].includes(rcaStatus) &&
        ['completed', 'skipped', 'failed'].includes(smartStatus);

      setStatus((prev) => {
        // Only log if there's an actual status change
        if (prev.rcaStatus !== rcaStatus || prev.smartStatus !== smartStatus) {
          console.log('[useRCAPolling] Status changed:', {
            rcaStatus,
            smartStatus,
            isGenerating,
            isComplete,
          });
        }

        return {
          rcaStatus,
          rcaCompletedAt: data.rca_generation_completed_at,
          smartStatus,
          smartCompletedAt: data.smart_goals_generation_completed_at,
          isGenerating,
          isComplete,
          lastChecked: new Date(),
        };
      });

      // Stop polling here (on fresh data) to avoid the race where Effect 2 fires
      // after Effect 1 has just recreated the interval (e.g., on session navigation).
      if (isComplete && intervalRef.current) {
        console.log('[useRCAPolling] All jobs complete - stopping polling');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch (err) {
      console.error('[useRCAPolling] Error checking status:', err);
    }
  }, [sessionId, enabled]);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!sessionId || !enabled) return;

    // Initial check
    checkStatus();

    // Set up polling interval
    intervalRef.current = setInterval(() => {
      checkStatus();
    }, POLL_INTERVAL_MS);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [sessionId, enabled, checkStatus]);

  return status;
}

/**
 * Helper function to get human-readable status message
 */
export function getRCAStatusMessage(status: string): string {
  switch (status) {
    case 'pending':
      return 'Queued for generation...';
    case 'in_progress':
      return 'Generating...';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Generation failed';
    case 'skipped':
      return 'Not applicable';
    default:
      return 'Unknown status';
  }
}

/**
 * Helper function to get status color for UI
 */
export function getRCAStatusColor(status: string): string {
  switch (status) {
    case 'pending':
      return 'text-yellow-600';
    case 'in_progress':
      return 'text-blue-600';
    case 'completed':
      return 'text-green-600';
    case 'failed':
      return 'text-red-600';
    case 'skipped':
      return 'text-gray-500';
    default:
      return 'text-gray-600';
  }
}
